export type Sample = { x: number; y: number };

export type Epicycle = {
  /** DFT bin 0..N-1 */
  k: number;
  /** Signed harmonic (k or k-N) */
  freq: number;
  re: number;
  im: number;
  amp: number;
  phase: number;
};

export const SAMPLE_COUNT = 512;
export const MAX_TERMS = 200;

export function hypot(dx: number, dy: number): number {
  return Math.hypot(dx, dy);
}

export function pathLength(points: readonly Sample[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

export function closePath(points: readonly Sample[]): Sample[] {
  if (points.length === 0) return [];
  const pts = points.map((p) => ({ x: p.x, y: p.y }));
  const a = pts[0];
  const b = pts[pts.length - 1];
  if (hypot(a.x - b.x, a.y - b.y) > 1e-6) pts.push({ x: a.x, y: a.y });
  return pts;
}

/** Uniform arc-length samples of a closed polyline. */
export function resampleClosed(points: readonly Sample[], n: number): Sample[] {
  if (n <= 0) return [];
  if (points.length === 0) return [];
  if (points.length === 1) return Array.from({ length: n }, () => ({ x: points[0].x, y: points[0].y }));

  const pts = closePath(points);
  const segs: { ax: number; ay: number; bx: number; by: number; len: number }[] = [];
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const len = hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    segs.push({ ax: pts[i].x, ay: pts[i].y, bx: pts[i + 1].x, by: pts[i + 1].y, len });
    total += len;
  }
  if (total === 0) {
    return Array.from({ length: n }, () => ({ x: pts[0].x, y: pts[0].y }));
  }

  const out: Sample[] = [];
  for (let i = 0; i < n; i++) {
    let d = (i / n) * total;
    let hit = false;
    for (let s = 0; s < segs.length; s++) {
      const seg = segs[s];
      if (d <= seg.len || s === segs.length - 1) {
        const t = seg.len === 0 ? 0 : Math.min(1, Math.max(0, d / seg.len));
        out.push({
          x: seg.ax + (seg.bx - seg.ax) * t,
          y: seg.ay + (seg.by - seg.ay) * t,
        });
        hit = true;
        break;
      }
      d -= seg.len;
    }
    if (!hit) out.push({ x: pts[0].x, y: pts[0].y });
  }
  return out;
}

export function dft(samples: readonly Sample[]): Epicycle[] {
  const N = samples.length;
  const out: Epicycle[] = [];
  if (N === 0) return out;

  for (let k = 0; k < N; k++) {
    let re = 0;
    let im = 0;
    for (let n = 0; n < N; n++) {
      const theta = (-2 * Math.PI * k * n) / N;
      const c = Math.cos(theta);
      const s = Math.sin(theta);
      re += samples[n].x * c - samples[n].y * s;
      im += samples[n].x * s + samples[n].y * c;
    }
    re /= N;
    im /= N;
    const amp = Math.hypot(re, im);
    const phase = Math.atan2(im, re);
    const freq = k === 0 ? 0 : k <= N / 2 ? k : k - N;
    out.push({ k, freq, re, im, amp, phase });
  }
  return out;
}

export type OrderMode = "amp" | "freq";

/** DC first (the chain's centre), then rotating terms. */
export function orderEpicycles(all: readonly Epicycle[], mode: OrderMode): Epicycle[] {
  const dc: Epicycle[] = [];
  const rest: Epicycle[] = [];
  for (const e of all) {
    if (e.freq === 0) dc.push(e);
    else rest.push(e);
  }
  if (mode === "amp") {
    rest.sort((a, b) => b.amp - a.amp || Math.abs(a.freq) - Math.abs(b.freq));
  } else {
    rest.sort((a, b) => {
      const af = Math.abs(a.freq);
      const bf = Math.abs(b.freq);
      if (af !== bf) return af - bf;
      return b.freq - a.freq;
    });
  }
  return dc.concat(rest);
}

/**
 * Positions after each term at time t ∈ [0, 1).
 * Index 0 is the DC centre. Later points are tips of successive rotating circles.
 */
export function chain(epis: readonly Epicycle[], t: number, rotatingCount: number): Sample[] {
  const pts: Sample[] = [];
  let x = 0;
  let y = 0;
  if (epis.length === 0) return pts;

  const dc = epis[0];
  const ang0 = dc.phase + 2 * Math.PI * dc.freq * t;
  x += dc.amp * Math.cos(ang0);
  y += dc.amp * Math.sin(ang0);
  pts.push({ x, y });

  const limit = Math.min(rotatingCount, Math.max(0, epis.length - 1));
  for (let i = 1; i <= limit; i++) {
    const e = epis[i];
    const ang = e.phase + 2 * Math.PI * e.freq * t;
    x += e.amp * Math.cos(ang);
    y += e.amp * Math.sin(ang);
    pts.push({ x, y });
  }
  return pts;
}

export function reconstruct(epis: readonly Epicycle[], n: number, rotatingCount: number): Sample[] {
  const out: Sample[] = [];
  const count = rotatingCount;
  for (let i = 0; i < n; i++) {
    const pts = chain(epis, i / n, count);
    out.push(pts[pts.length - 1] ?? { x: 0, y: 0 });
  }
  return out;
}

export function meanAbsError(a: readonly Sample[], b: readonly Sample[]): number {
  const n = Math.min(a.length, b.length);
  if (n === 0) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) s += hypot(a[i].x - b[i].x, a[i].y - b[i].y);
  return s / n;
}

export function fitPath(
  points: readonly Sample[],
  width: number,
  height: number,
  pad: number,
): Sample[] {
  if (points.length === 0) return [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const bw = Math.max(maxX - minX, 1e-6);
  const bh = Math.max(maxY - minY, 1e-6);
  const innerW = Math.max(width - pad * 2, 1);
  const innerH = Math.max(height - pad * 2, 1);
  const scale = Math.min(innerW / bw, innerH / bh);
  const ox = pad + (innerW - bw * scale) / 2;
  const oy = pad + (innerH - bh * scale) / 2;
  return points.map((p) => ({
    x: ox + (p.x - minX) * scale,
    y: oy + (p.y - minY) * scale,
  }));
}

export function usedAmplitude(epis: readonly Epicycle[], rotatingCount: number): { used: number; total: number } {
  let total = 0;
  let used = 0;
  for (let i = 0; i < epis.length; i++) {
    total += epis[i].amp;
    if (i === 0 || i <= rotatingCount) used += epis[i].amp;
  }
  return { used, total };
}

export function captionForTerms(terms: number): string {
  if (terms <= 1) return "One spinning circle traces an ellipse — the loudest frequency.";
  if (terms <= 6) return "A handful of circles find the overall gesture of the drawing.";
  if (terms <= 24) return "The silhouette appears. Corners stay round without high frequencies.";
  if (terms <= 80) return "Sharp turns start to form. Each new circle is a higher note.";
  if (terms <= 140) return "Fine wiggles lock in. You are hearing the treble of the shape.";
  return "Two hundred epicycles. The original drawing is a chord of circles.";
}
